import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { MagnifyingGlass, Users, Briefcase, NotePencil, Receipt, Sparkle, User as UserIcon } from "@phosphor-icons/react";

/**
 * GlobalSearch — top-bar Cmd/Ctrl+K quick jump.
 *
 * Fires GET /search?q=... on every keystroke (debounced 180ms) and renders
 * four grouped result buckets: Clients · Engagements · Notes · Invoices.
 * Every result is a real record — no synthetic entries — and clicking one
 * navigates to its detail page (or opens it inside the containing
 * engagement's Invoices/Notes tab). Keyboard: ↑/↓ moves the selection,
 * Enter opens, Esc closes.
 */
export default function GlobalSearch({ open, onOpenChange }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(0);
  const navigate = useNavigate();
  const inputRef = useRef(null);

  // Focus the input as soon as the dialog paints so the palette feels
  // instant. Reset state when the dialog closes so the next open is fresh.
  useEffect(() => {
    if (open) {
      setQ("");
      setResults(null);
      setSelected(0);
      setTimeout(() => inputRef.current?.focus(), 20);
    }
  }, [open]);

  // Debounced search — 180ms is short enough to feel typed-through but keeps
  // the network + regex load off the backend for fast typists.
  useEffect(() => {
    if (!open) return;
    const trimmed = q.trim();
    if (trimmed.length < 2) { setResults(null); return; }
    setLoading(true);
    const handle = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search?q=${encodeURIComponent(trimmed)}`);
        setResults(data);
        setSelected(0);
      } catch (e) {
        setResults({ clients: [], engagements: [], notes: [], invoices: [] });
      } finally {
        setLoading(false);
      }
    }, 180);
    return () => clearTimeout(handle);
  }, [q, open]);

  // Flatten grouped results into a single navigable list so ↑/↓ can march
  // across bucket boundaries. Each entry carries its bucket label and target
  // route so the render + keyboard handler share one source of truth.
  const flat = useMemo(() => {
    if (!results) return [];
    const out = [];
    for (const c of results.clients || []) {
      out.push({ kind: "client", id: c.id, label: c.name, sub: [c.industry, c.relationship_health].filter(Boolean).join(" · "), to: `/clients/${c.id}` });
    }
    for (const e of results.engagements || []) {
      out.push({ kind: "engagement", id: e.id, label: e.name, sub: [e.engagement_type, e.stage, e.status].filter(Boolean).join(" · "), to: `/engagements/${e.id}` });
    }
    for (const n of results.notes || []) {
      const to = n.engagement_id
        ? `/engagements/${n.engagement_id}?tab=notes`
        : n.client_id
        ? `/clients/${n.client_id}?tab=notes`
        : "/notes";
      const tags = new Set(n.tags || []);
      const lens = tags.has("connections-lens") ? "connections" : tags.has("leadership-lens") ? "leadership" : null;
      out.push({ kind: "note", id: n.id, label: n.title, sub: [n.note_type, (n.created_at || "").slice(0, 10)].filter(Boolean).join(" · "), to, lens });
    }
    for (const inv of results.invoices || []) {
      const to = inv.engagement_id
        ? `/engagements/${inv.engagement_id}?tab=invoices`
        : "/invoices";
      out.push({ kind: "invoice", id: inv.id, label: inv.invoice_number, sub: [inv.status, inv.issue_date, inv.total ? `$${Number(inv.total).toLocaleString()}` : null].filter(Boolean).join(" · "), to });
    }
    // Iter 85.5 · Firm-Wide Contact Search — every contact across every
    // client is reachable from the palette. Clicking opens the contact
    // history drawer via the URL param the ContactsPanel listens for.
    for (const c of results.contacts || []) {
      const label = c.name || "(unnamed contact)";
      const sub = [c.client_name, c.title, c.email].filter(Boolean).join(" · ");
      out.push({
        kind: "contact",
        id: c.id,
        label,
        sub,
        to: `/clients/${c.client_id}?tab=stakeholders&openContact=${c.id}`,
      });
    }
    return out;
  }, [results]);

  const go = (item) => {
    if (!item) return;
    navigate(item.to);
    onOpenChange(false);
  };

  const onKeyDown = (ev) => {
    if (!flat.length) return;
    if (ev.key === "ArrowDown") { ev.preventDefault(); setSelected((s) => (s + 1) % flat.length); }
    else if (ev.key === "ArrowUp") { ev.preventDefault(); setSelected((s) => (s - 1 + flat.length) % flat.length); }
    else if (ev.key === "Enter") { ev.preventDefault(); go(flat[selected]); }
  };

  const iconFor = (kind) => ({ client: Users, engagement: Briefcase, note: NotePencil, invoice: Receipt, contact: UserIcon })[kind] || Users;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none bg-card max-w-2xl p-0 overflow-hidden" data-testid="global-search-dialog">
        <DialogTitle className="sr-only">Search YadidOS</DialogTitle>
        <DialogDescription className="sr-only">Find any client, engagement, memo, or invoice by name.</DialogDescription>
        <div className="flex items-center gap-3 border-b border-border px-4 py-3">
          <MagnifyingGlass size={16} weight="bold" className="text-muted-foreground shrink-0" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search partners, engagements, memos, invoices…"
            className="flex-1 bg-transparent border-0 outline-none text-sm placeholder:text-muted-foreground"
            data-testid="global-search-input"
          />
          <kbd className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground border border-border px-1.5 py-0.5">esc</kbd>
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {q.trim().length < 2 && (
            <div className="px-4 py-10 text-center text-xs text-muted-foreground">Type at least two characters to search.</div>
          )}
          {q.trim().length >= 2 && loading && !flat.length && (
            <div className="px-4 py-10 text-center text-xs text-muted-foreground">Searching…</div>
          )}
          {q.trim().length >= 2 && !loading && results && flat.length === 0 && (
            <div className="px-4 py-10 text-center text-xs text-muted-foreground" data-testid="global-search-empty">Nothing matches "{q.trim()}".</div>
          )}
          {(["client", "engagement", "note", "invoice", "contact"]).map((kind) => {
            const items = flat.filter((f) => f.kind === kind);
            if (!items.length) return null;
            const label = { client: "Partners", engagement: "Engagements", note: "Notes & memos", invoice: "Invoices", contact: "Contacts" }[kind];
            const Icon = iconFor(kind);
            return (
              <div key={kind} data-testid={`global-search-group-${kind}`}>
                <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground px-4 pt-3 pb-1">{label}</div>
                {items.map((item) => {
                  const idx = flat.indexOf(item);
                  const active = idx === selected;
                  return (
                    <button
                      key={`${item.kind}-${item.id}`}
                      onMouseEnter={() => setSelected(idx)}
                      onClick={() => go(item)}
                      data-testid={`global-search-result-${item.kind}-${item.id}`}
                      className={`w-full text-left px-4 py-2.5 flex items-start gap-3 border-l-2 transition-colors ${active ? "border-primary bg-secondary/50" : "border-transparent hover:bg-secondary/30"}`}
                    >
                      <Icon size={14} weight="bold" className={`shrink-0 mt-0.5 ${active ? "text-primary" : "text-muted-foreground"}`} />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium truncate flex items-center gap-1.5">
                          {item.label}
                          {item.lens && (
                            <span
                              data-testid={`global-search-lens-pill-${item.id}`}
                              className="text-[0.5625rem] font-mono uppercase tracking-widest border border-brass/60 bg-brass-soft/40 text-brass px-1.5 py-0.5 inline-flex items-center gap-0.5"
                            >
                              <Sparkle size={8} weight="fill" />
                              {item.lens === "connections" ? "Connections" : "Leadership"}
                            </span>
                          )}
                        </div>
                        {item.sub && <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground truncate">{item.sub}</div>}
                      </div>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
